import { GOOGLE_MAPS_API_KEY } from "@/constants/env.const";
import { useCallback, useEffect, useMemo, useState } from "react";
import { GoogleMap, useJsApiLoader } from "@react-google-maps/api";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import styles from "./StatisticsAccountsMap.module.scss";
import type { StatisticsAccountGeoPoint } from "../../_types/statistics-api.types";
import { MAP_LIGHT_STYLES } from "./mapDarkStyles";
import { getPrimaryPinIcon } from "./primaryPinIcon";
import { VettyClusterRenderer } from "./vettyClusterRenderer";

const defaultCenter: google.maps.LatLngLiteral = {
  lat: 51.75,
  lng: 19.45,
};

const mapContainerStyle = { width: "100%", height: "100%" };

function MapMissingKeyFallback() {
  return (
    <div className={styles["comp__fallback"]}>
      <p className={styles["comp__fallback__title"]}>Map unavailable</p>
      <p className={styles["comp__fallback__text"]}>
        Set{" "}
        <code className={styles["comp__fallback__code"]}>
          VITE_GOOGLE_MAPS_API_KEY
        </code>{" "}
        in your environment, then restart the dev server.
      </p>
    </div>
  );
}

type StatisticsAccountsMapInnerProps = {
  geoPoints: StatisticsAccountGeoPoint[];
};

function StatisticsAccountsMapInner({ geoPoints }: StatisticsAccountsMapInnerProps) {
  const apiKey = GOOGLE_MAPS_API_KEY;
  const { isLoaded, loadError } = useJsApiLoader({
    id: "statistics-google-maps",
    googleMapsApiKey: apiKey,
  });

  const [map, setMap] = useState<google.maps.Map | null>(null);

  const mapOptions = useMemo<google.maps.MapOptions>(
    () => ({
      disableDefaultUI: true,
      zoomControl: false,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      rotateControl: false,
      scaleControl: false,
      cameraControl: false,
      clickableIcons: false,
      styles: MAP_LIGHT_STYLES,
      backgroundColor: "#f3f3f6",
    }),
    [],
  );

  const onLoad = useCallback((m: google.maps.Map) => {
    setMap(m);
  }, []);

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  useEffect(() => {
    if (!map || !isLoaded) return;

    if (geoPoints.length === 0) {
      map.setCenter(defaultCenter);
      map.setZoom(6);
      return;
    }

    if (geoPoints.length === 1) {
      const p = geoPoints[0];
      map.setCenter({ lat: p.lat, lng: p.lng });
      map.setZoom(8);
      return;
    }

    const bounds = new google.maps.LatLngBounds();
    geoPoints.forEach((p) =>
      bounds.extend({ lat: p.lat, lng: p.lng }),
    );
    map.fitBounds(bounds, 48);
  }, [map, isLoaded, geoPoints]);

  useEffect(() => {
    if (!map || !isLoaded) return;

    const pinIcon = getPrimaryPinIcon();
    const markers = geoPoints.map(
      (p) =>
        new google.maps.Marker({
          position: { lat: p.lat, lng: p.lng },
          icon: pinIcon,
        }),
    );

    const clusterer = new MarkerClusterer({
      map,
      markers,
      renderer: new VettyClusterRenderer(),
    });

    return () => {
      clusterer.clearMarkers();
      markers.forEach((m) => {
        m.setMap(null);
      });
    };
  }, [map, isLoaded, geoPoints]);

  if (loadError) {
    return (
      <div className={styles["comp__map-wrap"]}>
        <div className={styles["comp__fallback"]}>
          <p className={styles["comp__fallback__title"]}>Could not load maps</p>
          <p className={styles["comp__fallback__text"]}>
            Check that the API key is valid and Maps JavaScript API is enabled.
          </p>
        </div>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className={styles["comp__map-wrap"]}>
        <div className={styles["comp__loading"]} aria-hidden>
          Loading map…
        </div>
      </div>
    );
  }

  return (
    <div className={styles["comp__map-wrap"]}>
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={defaultCenter}
        zoom={6}
        onLoad={onLoad}
        onUnmount={onUnmount}
        options={mapOptions}
      />
    </div>
  );
}

type StatisticsAccountsMapProps = {
  geoPoints: StatisticsAccountGeoPoint[] | null;
};

export function StatisticsAccountsMap({ geoPoints }: StatisticsAccountsMapProps) {
  const apiKey = GOOGLE_MAPS_API_KEY;

  if (!apiKey) {
    return (
      <div className={styles["comp"]}>
        <div className={styles["comp__map-wrap"]}>
          <MapMissingKeyFallback />
        </div>
      </div>
    );
  }

  if (geoPoints === null) {
    return (
      <div className={styles["comp"]}>
        <div className={styles["comp__map-wrap"]}>
          <div className={styles["comp__loading"]} aria-hidden>
            Loading locations…
          </div>
        </div>
      </div>
    );
  }

  if (geoPoints.length === 0) {
    return (
      <div className={styles["comp"]}>
        <div className={styles["comp__map-wrap"]}>
          <div className={styles["comp__fallback"]}>
            <p className={styles["comp__fallback__title"]}>No locations</p>
            <p className={styles["comp__fallback__text"]}>
              No account locations to show yet (logins with geo or profile
              country).
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles["comp"]}>
      <StatisticsAccountsMapInner geoPoints={geoPoints} />
    </div>
  );
}
